import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../services/apiClient';
import type { ActivityFormData, ActivityPayload, ActivityPointForm } from '../../types/activity';
import { formToPayload } from './activityForm';
import type { ActivityFormField, ActivityImageValue } from './ActivityFormFields';
import {
  buildActivityErrorToast,
  mapServerErrors,
  validateActivityPayload,
  type ActivityFieldErrors,
} from './validateActivityForm';

/**
 * Form state shared by the create and edit panels: field updates, errors, dirty tracking
 * (reported to the page and guarded on browser unload) and client/server validation.
 */
export function useActivityForm(initial: () => ActivityFormData, onDirtyChange?: (dirty: boolean) => void) {
  const [formData, setFormData] = useState<ActivityFormData>(initial);
  const [errors, setErrors] = useState<ActivityFieldErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [imageValue, setImageValue] = useState<ActivityImageValue | null>(null);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const clearError = useCallback((...keys: string[]) => {
    setErrors((prev) => {
      if (!keys.some((k) => k in prev)) return prev;
      const next = { ...prev };
      for (const k of keys) delete next[k];
      return next;
    });
  }, []);

  const onFieldChange = useCallback(
    <K extends ActivityFormField>(field: K, value: ActivityFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);
      clearError(field);
    },
    [clearError],
  );

  const onLevelChange = useCallback(
    (level: number, points: ActivityPointForm[]) => {
      setFormData((prev) => ({ ...prev, levels: { ...prev.levels, [level]: points } }));
      setIsDirty(true);
      clearError(`points.level${level}`, 'points');
    },
    [clearError],
  );

  const onImageChange = useCallback(
    (image: ActivityImageValue | null) => {
      setImageValue(image);
      onFieldChange('imageDocumentId', image?.documentId ?? null);
    },
    [onFieldChange],
  );

  /** Replace the whole form (new activity loaded or saved data refreshed) */
  const reset = useCallback((data: ActivityFormData, image: ActivityImageValue | null) => {
    setFormData(data);
    setImageValue(image);
    setErrors({});
    setIsDirty(false);
  }, []);

  /** Runs the shared rule set; shows errors inline + toast and returns null when invalid */
  const validate = useCallback((): ActivityPayload | null => {
    const payload = formToPayload(formData);
    const found = validateActivityPayload(payload);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      toast.error(buildActivityErrorToast(found));
      return null;
    }
    return payload;
  }, [formData]);

  /** Shows a 400 response's field errors inline; other errors are toasted by the mutation hooks */
  const handleServerError = useCallback((err: unknown, payload: ActivityPayload) => {
    if (err instanceof ApiError && err.status === 400) {
      const mapped = mapServerErrors(err.errors, payload);
      setErrors(mapped);
      toast.error(buildActivityErrorToast(mapped));
    }
  }, []);

  return {
    formData,
    errors,
    isDirty,
    setIsDirty,
    imageValue,
    onFieldChange,
    onLevelChange,
    onImageChange,
    reset,
    validate,
    handleServerError,
  };
}
